import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../service/API';
import NotificationModal from '../TicketComponents/NotificationModal';
import SearchableDropdown from '../TicketComponents/SearchableDropdown';
import { getApiErrorMessage } from '../../service/errorHandler';
import "../../styling/ManageTicketAssign.css";

export default function ManageAssignments() {
  const navigate = useNavigate();
  const { ruleCategory } = useParams();
  const api = useApiClient();

  const [tab, setTab] = useState(() => {
    switch ((ruleCategory || '').toUpperCase()) {
      case 'USER':
      case 'DOMAIN':
      case 'TIMELINE':
      case 'SERVICE':
        return ruleCategory.toUpperCase();
      default:
        return 'USER';
    }
  });  const [foUsers, setFoUsers] = useState([]);
  const [boUsers, setBoUsers] = useState([]);
  const [userRules, setUserRules] = useState([]);
  const [domainRules, setDomainRules] = useState([]);
  const [timelineRules, setTimelineRules] = useState([]);
  const [serviceRules, setServiceRules] = useState([]);
console.log(timelineRules);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [creatorEmail, setCreatorEmail] = useState('');
  const [boUserEmail, setBoUserEmail] = useState('');
  const [domain, setDomain] = useState('');
  const [timelineSource, setTimelineSource] = useState('');
  const [timelineStart, setTimelineStart] = useState('');
  const [timelineEnd, setTimelineEnd] = useState('');
  const [service, setService] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const goBackToPreview = () => {
    navigate('/assignment-preview');
  };

  useEffect(() => {
    (async () => {
      try {
        const [fo, bo, ur, dr, tr, sr] = await Promise.all([
          api.get('/users/fouser-list'),
          api.get('/users/bouser-list'),
          api.get('/assignments/users'),
          api.get('/assignments/domains'),
          api.get('/assignments/time-line'),
          api.get('/assignments/service'),
        ]);

        setFoUsers(fo.data || []);
        setBoUsers(bo.data || []);
        setUserRules(ur.data || []);
        setDomainRules(dr.data || []);
        setTimelineRules(tr.data || []);
        setServiceRules(sr.data || []);
      } catch (e) {
        console.error(e);
        setModalMessage('Failed to load data.');
        setShowModal(true);
      }
    })();
  }, []);

  const findEmail = (id, list) => list.find(u => u.id === id)?.email || id;
  const getFoByEmail = (email) => foUsers.find(u => u.email === email);
  const getBoByEmail = (email) => boUsers.find(u => u.email === email);

  const createUserRule = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const creator = getFoByEmail(creatorEmail);
      const boUser = getBoByEmail(boUserEmail);

      if (!creator || !boUser) {
        throw new Error('Please select a valid FO user and BO user.');
      }

      const creatorId = creator.id;
      const boUserId = boUser.id;

      const dup = userRules.some(r => r.creatorId === creatorId && r.boUserId === boUserId);
      if (dup) throw new Error('This user rule already exists.');

      await api.post('/assignments/users', { creatorId, boUserId });
      const { data } = await api.get('/assignments/users');

      setUserRules(data || []);
      setCreatorEmail('');
      setBoUserEmail('');

      setModalMessage('User rule created.');
      setShowModal(true);
    } catch (err) {
      setModalMessage(getApiErrorMessage(err, 'Failed to create user rule.'));
      setShowModal(true);
    } finally {
      setCreating(false);
    }
  };

  const createDomainRule = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const d = (domain || '').trim().toLowerCase();

      if (!d || !d.includes('.') || d.includes(' ')) {
        throw new Error('Please provide a valid domain, e.g. "alpha.com".');
      }

      const boUser = getBoByEmail(boUserEmail);
      if (!boUser) throw new Error('Please select a valid BO user.');

      const boUserId = boUser.id;

      const dup = domainRules.some(
        r => (r.domain || '').toLowerCase() === d && r.boUserId === boUserId
      );
      if (dup) throw new Error('This domain rule already exists.');

      await api.post('/assignments/domains', { domain: d, boUserId });
      const { data } = await api.get('/assignments/domains');

      setDomainRules(data || []);
      setDomain('');
      setBoUserEmail('');

      setModalMessage('Domain rule created.');
      setShowModal(true);
    } catch (err) {
      setModalMessage(getApiErrorMessage(err, 'Failed to create domain rule.'));
      setShowModal(true);
    } finally {
      setCreating(false);
    }
  };

  const createTimelineRule = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const source = (timelineSource || '').trim();

      if (!source) throw new Error('Please provide a timeline source.');
      if (!timelineStart || !timelineEnd) throw new Error('Please provide start and end times.');
      if (timelineStart >= timelineEnd) throw new Error('Start time must be before end time.');

      const boUser = getBoByEmail(boUserEmail);
      if (!boUser) throw new Error('Please select a valid BO user.');

      const boUserId = boUser.id;

      const dup = timelineRules.some(
        r =>
          r.source === source &&
          r.start === timelineStart &&
          r.end === timelineEnd &&
          r.boUserId === boUserId
      );
      if (dup) throw new Error('This timeline rule already exists.');

      await api.post('/assignments/time-line', {
        source,
        start: timelineStart,
        end: timelineEnd,
        boUserId,
      });

      const { data } = await api.get('/assignments/time-line');

      setTimelineRules(data || []);
      setTimelineSource('');
      setTimelineStart('');
      setTimelineEnd('');
      setBoUserEmail('');

      setModalMessage('Timeline rule created.');
      setShowModal(true);
    } catch (err) {
      setModalMessage(getApiErrorMessage(err, 'Failed to create timeline rule.'));
      setShowModal(true);
    } finally {
      setCreating(false);
    }
  };

  const createServiceRule = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const serviceValue = (service || '').trim();

      if (!serviceValue) throw new Error('Please provide a service.');

      const boUser = getBoByEmail(boUserEmail);
      if (!boUser) throw new Error('Please select a valid BO user.');

      const boUserId = boUser.id;

      const dup = serviceRules.some(
        r =>
          (r.source || '').toLowerCase() === serviceValue.toLowerCase() &&
          r.boUserId === boUserId
      );
      if (dup) throw new Error('This service rule already exists.');

      await api.post('/assignments/service', {
        service: serviceValue,
        boUserId,
      });

      const { data } = await api.get('/assignments/service');

      setServiceRules(data || []);
      setService('');
      setBoUserEmail('');

      setModalMessage('Service rule created.');
      setShowModal(true);
    } catch (err) {
      setModalMessage(getApiErrorMessage(err, 'Failed to create service rule.'));
      setShowModal(true);
    } finally {
      setCreating(false);
    }
  };

  const deleteRule = async (id, kind) => {
    setDeletingId(id);

    try {
      await api.delete(`/assignments/${id}`);

      if (kind === 'USER') setUserRules(userRules.filter(r => r.id !== id));
      else if (kind === 'DOMAIN') setDomainRules(domainRules.filter(r => r.id !== id));
      else if (kind === 'TIMELINE') setTimelineRules(timelineRules.filter(r => r.id !== id));
      else if (kind === 'SERVICE') setServiceRules(serviceRules.filter(r => r.id !== id));

      setModalMessage('Rule deleted.');
      setShowModal(true);
    } catch {
      setModalMessage('Failed to delete rule.');
      setShowModal(true);
    } finally {
      setDeletingId(null);
    }
  };

  const foOptions = foUsers.map(u => ({
    fullname: u.fullname || u.name || u.email,
    email: u.email,
  }));

  const boOptions = boUsers.map(u => ({
    fullname: u.fullname || u.name || u.email,
    email: u.email,
  }));

  const renderAssignToDropdown = (id) => (
    <SearchableDropdown
      id={id}
      options={boOptions}
      selectedValue={boUserEmail}
      onSelectedChange={setBoUserEmail}
      placeholder="Select BO user"
    />
  );

  return (
    <div className="ma-container">
      <div className="ma-header">
        <h2 className="ma-title">Manage Assignments</h2>

        <button
          type="button"
          className="ma-btn"
          onClick={goBackToPreview}
        >
          Back to Preview
        </button>
      </div>

      <NotificationModal
        show={showModal}
        message={modalMessage}
        onClose={() => setShowModal(false)}
      />

      <div className="ma-tabs">
        <button
          className={`ma-tabBtn ${tab === 'USER' ? 'ma-tabBtn--active' : ''}`}
          onClick={() => setTab('USER')}
          type="button"
        >
          By User
        </button>

        <button
          className={`ma-tabBtn ${tab === 'DOMAIN' ? 'ma-tabBtn--active' : ''}`}
          onClick={() => setTab('DOMAIN')}
          type="button"
        >
          By Company
        </button>

        <button
          className={`ma-tabBtn ${tab === 'TIMELINE' ? 'ma-tabBtn--active' : ''}`}
          onClick={() => setTab('TIMELINE')}
          type="button"
        >
          By Timeline
        </button>

        <button
          className={`ma-tabBtn ${tab === 'SERVICE' ? 'ma-tabBtn--active' : ''}`}
          onClick={() => setTab('SERVICE')}
          type="button"
        >
          By Service
        </button>
      </div>

      {tab === 'USER' && (
        <>
          <form onSubmit={createUserRule} className="ma-form">
            <div className="ma-formGroup">
              <label className="ma-label">Creator (FO)</label>
              <SearchableDropdown
                id="creator-fo"
                options={foOptions}
                selectedValue={creatorEmail}
                onSelectedChange={setCreatorEmail}
                placeholder="Select FO user"
              />
            </div>

            <div className="ma-formGroup">
              <label className="ma-label">Assign To (BO)</label>
              {renderAssignToDropdown('assign-bo-user')}
            </div>

            <button
              type="submit"
              className="ma-btn ma-btn--primary"
              disabled={creating || !creatorEmail || !boUserEmail}
            >
              {creating ? 'Saving...' : 'Save Rule'}
            </button>
          </form>

          <ul className="ma-list">
            {userRules.map(r => (
              <li key={r.id} className="ma-listItem">
                <span className="ma-listItem__meta">
                  Creator: <strong>{r.creatorEmail || findEmail(r.creatorId, foUsers)}</strong>
                  <br />
                  Assigned To:{' '}
                  <span className="ma-muted">
                    {r.boUserEmail || findEmail(r.boUserId, boUsers)}
                  </span>
                </span>

                <div className="ma-listItem__actions">
                  <button
                    onClick={() => deleteRule(r.id, 'USER')}
                    className="ma-btn ma-btn--danger"
                    disabled={deletingId === r.id}
                  >
                    {deletingId === r.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}

            {userRules.length === 0 && <p className="ma-empty">No user rules.</p>}
          </ul>
        </>
      )}

      {tab === 'DOMAIN' && (
        <>
          <form onSubmit={createDomainRule} className="ma-form">
            <div className="ma-formGroup">
              <label className="ma-label">Company Domain</label>
              <input
                className="ma-input"
                value={domain}
                onChange={e => setDomain(e.target.value)}
                placeholder="alpha.com"
                required
              />
            </div>

            <div className="ma-formGroup">
              <label className="ma-label">Assign To (BO)</label>
              {renderAssignToDropdown('assign-bo-domain')}
            </div>

            <button
              type="submit"
              className="ma-btn ma-btn--primary"
              disabled={creating || !domain || !boUserEmail}
            >
              {creating ? 'Saving...' : 'Save Rule'}
            </button>
          </form>

          <ul className="ma-list">
            {domainRules.map(r => (
              <li key={r.id} className="ma-listItem">
                <span className="ma-listItem__meta">
                  Domain: <strong>{r.domain}</strong>
                  <br />
                  Assigned To:{' '}
                  <span className="ma-muted">
                    {r.boUserEmail || findEmail(r.boUserId, boUsers)}
                  </span>
                </span>

                <div className="ma-listItem__actions">
                  <button
                    onClick={() => deleteRule(r.id, 'DOMAIN')}
                    className="ma-btn ma-btn--danger"
                    disabled={deletingId === r.id}
                  >
                    {deletingId === r.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}

            {domainRules.length === 0 && <p className="ma-empty">No domain rules.</p>}
          </ul>
        </>
      )}

      {tab === 'TIMELINE' && (
        <>
          <form onSubmit={createTimelineRule} className="ma-form">
            <div className="ma-formGroup">
              <label className="ma-label">Timeline Source</label>
              <input
                className="ma-input"
                value={timelineSource}
                onChange={e => setTimelineSource(e.target.value)}
                placeholder="e.g. BUSINESS_HOURS"
                required
              />
            </div>

            <div className="ma-formGroup">
              <label className="ma-label">Start Time</label>
              <input
                className="ma-input"
                type="time"
                value={timelineStart}
                onChange={e => setTimelineStart(e.target.value)}
                required
              />
            </div>

            <div className="ma-formGroup">
              <label className="ma-label">End Time</label>
              <input
                className="ma-input"
                type="time"
                value={timelineEnd}
                onChange={e => setTimelineEnd(e.target.value)}
                required
              />
            </div>

            <div className="ma-formGroup">
              <label className="ma-label">Assign To (BO)</label>
              {renderAssignToDropdown('assign-bo-timeline')}
            </div>

            <button
              type="submit"
              className="ma-btn ma-btn--primary"
              disabled={creating || !timelineSource || !timelineStart || !timelineEnd || !boUserEmail}
            >
              {creating ? 'Saving...' : 'Save Rule'}
            </button>
          </form>

          <ul className="ma-list">
            {timelineRules.map(r => (
              <li key={r.id} className="ma-listItem">
                <span className="ma-listItem__meta">
                  Source: <strong>{r.source}</strong>
                  <br />
                  Time:{' '}
                  <span className="ma-muted">
                    {r.start || '-'} - {r.end || '-'}
                  </span>
                  <br />
                  Assigned To:{' '}
                  <span className="ma-muted">
                    {r.boUserEmail || findEmail(r.boUserId, boUsers)}
                  </span>
                </span>

                <div className="ma-listItem__actions">
                  <button
                    onClick={() => deleteRule(r.id, 'TIMELINE')}
                    className="ma-btn ma-btn--danger"
                    disabled={deletingId === r.id}
                  >
                    {deletingId === r.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}

            {timelineRules.length === 0 && <p className="ma-empty">No timeline rules.</p>}
          </ul>
        </>
      )}

      {tab === 'SERVICE' && (
        <>
          <form onSubmit={createServiceRule} className="ma-form">
            <div className="ma-formGroup">
              <label className="ma-label">Service</label>
              <input
                className="ma-input"
                value={service}
                onChange={e => setService(e.target.value)}
                placeholder="e.g. INSTALLATION"
                required
              />
            </div>

            <div className="ma-formGroup">
              <label className="ma-label">Assign To (BO)</label>
              {renderAssignToDropdown('assign-bo-service')}
            </div>

            <button
              type="submit"
              className="ma-btn ma-btn--primary"
              disabled={creating || !service || !boUserEmail}
            >
              {creating ? 'Saving...' : 'Save Rule'}
            </button>
          </form>

          <ul className="ma-list">
            {serviceRules.map(r => (
              <li key={r.id} className="ma-listItem">
                <span className="ma-listItem__meta">
                  Service: <strong>{r.source}</strong>
                  <br />
                  Assigned To:{' '}
                  <span className="ma-muted">
                    {r.boUserEmail || findEmail(r.boUserId, boUsers)}
                  </span>
                </span>

                <div className="ma-listItem__actions">
                  <button
                    onClick={() => deleteRule(r.id, 'SERVICE')}
                    className="ma-btn ma-btn--danger"
                    disabled={deletingId === r.id}
                  >
                    {deletingId === r.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}

            {serviceRules.length === 0 && <p className="ma-empty">No service rules.</p>}
          </ul>
        </>
      )}

    </div>
  );
}